import { computed, ref, watch } from 'vue';
import { debounce } from 'lodash';
import _ from 'lodash';
import { useI18n } from '@/composables';
import {
  EDIT_ACCESS_TABS,
  TABS_MAP,
  buildAccessObj,
  buildTabsData,
  getDefaultItemsRelationsObject,
  getDefaultFilteredRowsByTab,
  getDefaultUserAlc,
  tabsSettings,
  type EditAccessTab,
  type PermissionsDialogMember,
  type PermissionsDialogWorkspace,
} from './useEditAccessPermissionsConstants';

export const useEditAccessPermissions = () => {
  const { t } = useI18n();

  const showSelectForId = ref<string | null>(null);
  const active = ref(false);
  const userPermissions = ref<Record<string, unknown> | null>(null);
  const rows = ref<Record<string, unknown[]>>({});
  const search = ref('');
  const itemsRelationsObject = ref<Record<string, Record<string, unknown>>>({});
  const tab = ref<EditAccessTab>('campaigns');
  const user = ref<PermissionsDialogMember>({ id: '', login: '', role: '' });
  const activeWorkspace = ref<PermissionsDialogWorkspace>({ id: '', name: '' });
  const filteredRowsByTab = ref(getDefaultFilteredRowsByTab());

  const accessObj = computed(() => buildAccessObj(t));
  const access = computed(() => Object.values(accessObj.value));
  const accessAll = computed(() => access.value.filter((item) => item.value !== 'inherit'));

  const tabsData = computed(() => buildTabsData(t));

  const dialogContentClass = computed(() => {
    if (userPermissions.value?.accessToExistingItems === 'onlySelected') {
      return 'access-permissions-dialog access-permissions-dialog--onlySelected';
    }
    return 'access-permissions-dialog';
  });

  const header = computed(() => (tabsData.value[tab.value] ? tabsData.value[tab.value].header : ''));

  const currentAcl = computed(() => {
    const permissions = userPermissions.value as { acl: Record<string, Record<string, string>> };
    return permissions.acl[TABS_MAP[tab.value]];
  });

  const dependConstraint = (resourceId: string, tabKey: string = tab.value) => {
    const entityTabsSettings = tabsSettings.find((item) => item.plural === tabKey);
    let result: string | null = null;

    if (!entityTabsSettings) {
      return null;
    }

    const depends =
      itemsRelationsObject.value[entityTabsSettings.plural]?.[resourceId] &&
      (itemsRelationsObject.value[entityTabsSettings.plural][resourceId].depends as Record<
        string,
        string[]
      >);

    if (!depends) {
      return null;
    }

    const forcedPermissions: {
      category: (typeof tabsSettings)[number];
      resourceId: string;
      accessType: string;
    }[] = [];

    const permissions = userPermissions.value as { acl: Record<string, Record<string, string>> };

    _.forOwn(depends, (value: string[], key: string) => {
      const currentTabsSettings = tabsSettings.find((item) => item.plural === key);

      if (!currentTabsSettings) {
        return;
      }

      value.forEach((entityId: string) => {
        const accessType = permissions.acl[currentTabsSettings.single][entityId];

        if (
          permissions.acl[entityTabsSettings.single][resourceId] !== 'fullAccess' &&
          ((accessType === 'reportsOnly' &&
            permissions.acl[entityTabsSettings.single][resourceId] !== 'readOnly') ||
            accessType === 'readOnly')
        ) {
          result = accessType;
          forcedPermissions.push({
            category: currentTabsSettings,
            resourceId: entityId,
            accessType,
          });
        }
      });
    });

    return result
      ? {
          accessType: result,
          forcedPermissions,
        }
      : null;
  };

  const genDependConstraint = (id: string, tabKey: string = tab.value) => {
    const constraint = dependConstraint(id, tabKey);

    if (!constraint) {
      return null;
    }

    return constraint.forcedPermissions.map((item) => {
      const relation = itemsRelationsObject.value[item.category.plural][item.resourceId] as {
        nameFull?: string;
        name: string;
      };
      const accessType = access.value.find((accessItem) => accessItem.value === item.accessType)?.text;

      return id === '*'
        ? t("{0} because it's used by all {1}", [
            accessType,
            tabsData.value[item.category.plural as EditAccessTab].title.toLowerCase(),
          ])
        : t("{0} because it's used by {1}", [
            accessType,
            relation.nameFull ? relation.nameFull : relation.name,
          ]);
    });
  };

  const penClick = (item: { id: string } | null) => {
    showSelectForId.value = item?.id ?? null;
  };

  const compareAccessPriority = (value: string, accessType: string) => {
    const constraintAccess = access.value.find((item) => item.value === accessType);
    const valueAccess = access.value.find((item) => item.value === value);

    if (!constraintAccess || !valueAccess) {
      return 0;
    }

    return valueAccess.priority < constraintAccess.priority
      ? 1
      : valueAccess.priority > constraintAccess.priority
        ? -1
        : 0;
  };

  const fixValue = (value: string, accessType: string) => {
    if (compareAccessPriority(value, accessType) === 1) {
      return accessType;
    }

    return value;
  };

  const fixEntity = (accessType: string, resourceId: string, tabKey: string) => {
    const permissions = userPermissions.value as { acl: Record<string, Record<string, string>> };

    const usage =
      itemsRelationsObject.value[tabKey]?.[resourceId] &&
      (itemsRelationsObject.value[tabKey][resourceId].uses as Record<string, unknown>);

    if (!usage) {
      return;
    }

    Object.keys(usage).forEach((key: string) => {
      let currentTabsSettings: (typeof tabsSettings)[number] | undefined;
      let entities: unknown[] = [];

      if (Array.isArray(usage[key])) {
        entities = usage[key] as unknown[];
        currentTabsSettings = tabsSettings.find((item) => item.plural === key);
      } else {
        entities = [usage[key]];
        currentTabsSettings = tabsSettings.find((item) => item.single === key);
      }

      if (!currentTabsSettings) {
        return;
      }

      entities.forEach((entity: string) => {
        const entityId = entity;
        if (permissions.acl[currentTabsSettings.single][entityId] !== 'fullAccess') {
          if (
            (accessType === 'reportsOnly' &&
              permissions.acl[currentTabsSettings.single][entityId] !== 'readOnly') ||
            accessType === 'readOnly'
          ) {
            permissions.acl[currentTabsSettings.single][entityId] = accessType;
            fixEntity(accessType, entityId, currentTabsSettings.plural);
          }
        }
      });
    });
  };

  const checkRelatedPermissions = (accessType: string, resourceId: string) => {
    fixEntity(accessType, resourceId, tab.value);
  };

  const onSelectChange = (event: string, item: { id: string } | string) => {
    const id = typeof item === 'string' ? item : item.id;
    const constraint = dependConstraint(id);
    const nextValue = constraint ? fixValue(event, constraint.accessType) : event;

    currentAcl.value[id] = nextValue;
    checkRelatedPermissions(currentAcl.value[id], id);
    penClick(null);
  };

  const fillRows = () => {
    const tabRows = rows.value[tab.value] || [];
    const filtered = tabRows.filter((item: Record<string, unknown>) => {
      const name = item.nameFull ? String(item.nameFull) : String(item.name);
      return name.toLowerCase().includes(search.value.toLowerCase());
    });
    filteredRowsByTab.value[tab.value] = filtered;
  };

  const fillRowsDebounced = debounce(fillRows, 300);

  const updateTabStatus = (tabKey: EditAccessTab) => {
    if (!rows.value[tabKey]) {
      return;
    }

    search.value = '';

    if (filteredRowsByTab.value[tabKey].length !== rows.value[tabKey].length) {
      filteredRowsByTab.value[tabKey] = rows.value[tabKey];
    }
  };

  const buildItemsRelations = (itemsRows: Record<string, unknown[]>) => {
    const relationsObject = getDefaultItemsRelationsObject() as Record<string, Record<string, unknown>>;
    const callbacks: (() => void)[] = [];

    _.forOwn(itemsRows, (items: Record<string, unknown>[], key: string) => {
      items.forEach((item: { id: string; uses?: Record<string, unknown> }) => {
        relationsObject[key][item.id] = item;

        const usage = item.uses as Record<string, unknown> | undefined;

        if (!usage) {
          return;
        }

        _.forOwn(usage, (usageItems: unknown, usageKey: string) => {
          let entities: unknown[] = [];
          let currentTabsSettings: (typeof tabsSettings)[number] | undefined;

          if (Array.isArray(usageItems)) {
            if (!usageItems.length) {
              return;
            }

            entities = usageItems;
            currentTabsSettings = tabsSettings.find((settingsItem) => settingsItem.plural === usageKey);
          } else {
            entities = [usageItems];
            currentTabsSettings = tabsSettings.find((settingsItem) => settingsItem.single === usageKey);
          }

          entities = entities.filter((entity) => !!entity);

          if (entities.length && currentTabsSettings) {
            callbacks.push(() => {
              entities.forEach((entity) => {
                const entityId = String(entity);
                const object = relationsObject[currentTabsSettings.plural][entityId] as {
                  depends?: Record<string, string[]>;
                };

                if (!object) {
                  return;
                }

                let stage = 0;
                let result: string[] =
                  ((stage = 1) && object.depends && (stage = 2) && object.depends[key]) || [];

                result = [...result, item.id];

                switch (stage) {
                  case 1:
                    object.depends = { [key]: result };
                    break;
                  case 2:
                    object.depends[key] = result;
                    break;
                }
              });
            });
          }
        });
      });
    });

    callbacks.forEach((callback) => callback());

    return relationsObject;
  };

  const resetPermissionsState = () => {
    itemsRelationsObject.value = getDefaultItemsRelationsObject() as Record<string, Record<string, unknown>>;
    userPermissions.value = getDefaultUserAlc();
    filteredRowsByTab.value = getDefaultFilteredRowsByTab();
  };

  watch(search, (value, oldValue) => {
    if (
      value !== oldValue ||
      (value === '' && filteredRowsByTab.value[tab.value].length < (rows.value[tab.value]?.length || 0))
    ) {
      fillRowsDebounced();
    }
  });

  watch(
    () => userPermissions.value?.hideProfit,
    (value) => {
      if (value === false && userPermissions.value) {
        userPermissions.value.showConversions = false;
      }
    },
  );

  watch(tab, (value) => {
    updateTabStatus(value);
  });

  watch(active, (value) => {
    if (value) {
      updateTabStatus(tab.value);
    }
  });

  return {
    tabs: EDIT_ACCESS_TABS,
    showSelectForId,
    active,
    userPermissions,
    rows,
    search,
    itemsRelationsObject,
    tab,
    user,
    activeWorkspace,
    filteredRowsByTab,
    accessObj,
    access,
    accessAll,
    tabsData,
    dialogContentClass,
    header,
    currentAcl,
    genDependConstraint,
    penClick,
    onSelectChange,
    updateTabStatus,
    buildItemsRelations,
    resetPermissionsState,
  };
};
